import { useState, useEffect, useRef } from 'react';
import { doc, updateDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../../../firebaseConfig';

// Helper to get milliseconds from timer startTime
const getStartTimeMs = (startTime) => {
  if (!startTime) return null;
  if (startTime instanceof Timestamp || (startTime.toMillis && typeof startTime.toMillis === 'function')) {
    return startTime.toMillis();
  }
  if (typeof startTime === 'number') {
    return startTime;
  }
  if (startTime.seconds !== undefined) {
    return startTime.seconds * 1000 + Math.floor((startTime.nanoseconds || 0) / 1000000);
  }
  return null;
};

// Manages turn timer: initialization, countdown, and expiration
export const useGameTimer = (gameId, game, playerTeam, isAdmin, onTimerExpired) => {
  const [remainingTime, setRemainingTime] = useState(null);
  const timerExpirationHandledRef = useRef(false);
  const latestGameRef = useRef(null);

  // Keep latest game ref updated
  useEffect(() => {
    latestGameRef.current = game;
  }, [game]);

  // Start or update turn timer
  const startTimer = async (team, phase, duration) => {
    try {
      const gameRef = doc(db, 'games', gameId);
      const timerField = `turnTimer.${team}`;

      await updateDoc(gameRef, {
        [timerField]: {
          startTime: serverTimestamp(),
          phase,
          duration
        }
      });
    } catch (error) {
      console.error('Error starting timer:', error);
    }
  };

  // GAME OVER EFFECT - clear timer when game ends
  useEffect(() => {
    if (game?.status && game.status !== 'in_progress') {
      setRemainingTime(null);
      timerExpirationHandledRef.current = true;
    }
  }, [game?.status]);

  // Initialize timer for current turn if needed
  useEffect(() => {
    if (!game || isAdmin || game.status !== 'in_progress') return;
    if (game.paused) return; // host paused — don't start/run a timer
    if (!playerTeam) return;

    const currentTurnTeam = game.currentTurn;
    if (currentTurnTeam !== playerTeam) return;

    const timerData = currentTurnTeam === 'white' ? game.turnTimer?.white : game.turnTimer?.black;

    if (!timerData || !timerData.startTime) {
      // Resume into the right phase: if a poll is already live, it's a voting
      // turn (30s); otherwise it's the suggestion phase (90s).
      const hasPoll = !!(currentTurnTeam === 'white' ? game.activePolls?.white : game.activePolls?.black);
      console.log('[TIMER] Initializing timer for', currentTurnTeam);
      startTimer(currentTurnTeam, hasPoll ? 'voting' : 'suggestion', hasPoll ? 30 : 90);
    } else {
      // Verify timer isn't stale
      const moveHistory = game.moveHistory || [];
      const lastMove = moveHistory[moveHistory.length - 1];
      if (lastMove?.timestamp && timerData.startTime) {
        const timerStartMs = getStartTimeMs(timerData.startTime);
        const lastMoveTime = new Date(lastMove.timestamp).getTime();

        if (timerStartMs && timerStartMs < lastMoveTime - 1000) {
          console.log('[TIMER] Timer is stale, reinitializing for', currentTurnTeam);
          startTimer(currentTurnTeam, 'suggestion', 90);
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game?.currentTurn, game?.status, game?.paused, isAdmin, playerTeam, game?.moveHistory?.length]);

  // Timer countdown effect
  useEffect(() => {
    if (!game || !playerTeam || isAdmin) return;

    if (game.status !== 'in_progress') {
      setRemainingTime(null);
      timerExpirationHandledRef.current = false;
      return;
    }

    // Host paused: freeze the countdown and never fire expiration.
    if (game.paused) {
      setRemainingTime(null);
      timerExpirationHandledRef.current = false;
      return;
    }

    if (game.currentTurn !== playerTeam) {
      setRemainingTime(null);
      timerExpirationHandledRef.current = false;
      return;
    }

    const timerData = playerTeam === 'white' ? game.turnTimer?.white : game.turnTimer?.black;

    if (!timerData || !timerData.startTime) {
      setRemainingTime(null);
      timerExpirationHandledRef.current = false;
      console.log('[TIMER] No timer data for', playerTeam, '- waiting for initialization');

      const retryTimeout = setTimeout(() => {
        const currentGame = latestGameRef.current;
        if (currentGame && !currentGame.paused && currentGame.currentTurn === playerTeam && currentGame.status === 'in_progress') {
          const currentTimerData = playerTeam === 'white' ? currentGame.turnTimer?.white : currentGame.turnTimer?.black;
          if (!currentTimerData || !currentTimerData.startTime) {
            const hasPoll = !!(playerTeam === 'white' ? currentGame.activePolls?.white : currentGame.activePolls?.black);
            console.log('[TIMER] Retry: Still no timer after delay, initializing for', playerTeam);
            startTimer(playerTeam, hasPoll ? 'voting' : 'suggestion', hasPoll ? 30 : 90);
          }
        }
      }, 2000);

      return () => clearTimeout(retryTimeout);
    }

    // Reset expiration flag when timer changes
    timerExpirationHandledRef.current = false;

    const interval = setInterval(() => {
      const currentGame = latestGameRef.current;

      if (!currentGame || currentGame.status !== 'in_progress' || currentGame.currentTurn !== playerTeam) {
        setRemainingTime(null);
        return;
      }

      if (currentGame.paused) {
        setRemainingTime(null);
        return;
      }

      const currentTimerData = playerTeam === 'white' ? currentGame.turnTimer?.white : currentGame.turnTimer?.black;
      if (!currentTimerData || !currentTimerData.startTime) {
        setRemainingTime(null);
        return;
      }

      const currentStartTimeMs = getStartTimeMs(currentTimerData.startTime);
      if (!currentStartTimeMs) {
        setRemainingTime(currentTimerData.duration);
        return;
      }

      const now = Date.now();
      const elapsed = (now - currentStartTimeMs) / 1000;
      const remaining = Math.max(0, Math.ceil(currentTimerData.duration - elapsed));

      setRemainingTime(remaining);

      if (remaining === 0 && !timerExpirationHandledRef.current && currentGame.status === 'in_progress' && currentGame.currentTurn === playerTeam) {
        console.log('[TIMER] EXPIRED! Handling expiration for', playerTeam);
        timerExpirationHandledRef.current = true;
        onTimerExpired();
      }
    }, 100);

    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game?.turnTimer, game?.currentTurn, game?.status, game?.paused, playerTeam, isAdmin]);

  return {
    remainingTime,
    setRemainingTime,
    startTimer,
  };
};
